import React from "react";
import { useLocation } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Meta from "@/components/Meta";

/**
 * Standard page shell.
 *
 * Props:
 *   title, description   fully formed page title and meta description
 *   ogImage              optional per-page OG image
 *   breadcrumbs          optional BreadcrumbList entries
 *   article              optional Article schema config for Thinking detail
 *   testId               data-testid for main
 *   children             page content
 */
export const PageShell = ({
  title,
  description,
  ogImage,
  breadcrumbs,
  article,
  jsonLd,
  noindex,
  children,
  testId,
}) => {
  const { pathname, hash } = useLocation();

  // Preserve anchor navigation on hash-linked entry; otherwise scroll to top on route change.
  React.useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      const t = window.setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return () => window.clearTimeout(t);
    }
    window.scrollTo({ top: 0 });
  }, [pathname, hash]);

  return (
    <div className="min-h-screen flex flex-col bg-ink-base">
      <Meta
        title={title}
        description={description}
        path={pathname}
        ogImage={ogImage}
        type={article ? "article" : "website"}
        breadcrumbs={breadcrumbs}
        article={article}
        jsonLd={jsonLd}
        noindex={noindex}
      />
      <Header />
      <main id="main-content" data-testid={testId} className="flex-1 pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export const SectionHeader = ({ eyebrow, title, lead, align = "left", className = "" }) => {
  const alignment = align === "center" ? "text-center mx-auto" : "text-left";
  return (
    <div className={`max-w-3xl ${alignment} ${className}`}>
      {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
      {title && (
        <h2 className="display-serif text-3xl md:text-5xl lg:text-6xl text-paper-primary text-balance">
          {title}
        </h2>
      )}
      {lead && (
        <p className="mt-6 text-paper-secondary text-lg md:text-xl leading-relaxed font-serif text-pretty">{lead}</p>
      )}
    </div>
  );
};

export const Rule = ({ className = "" }) => <div className={`hairline ${className}`} />;

export default PageShell;
